'use client';

import {
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Switch,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useLazyQuery } from '@parkchirp/api-client';
import { ReportType } from 'parkchirp-shared/enums';
import React, { FC, useCallback, useState } from 'react';
import { GET_SUBSCRIPTION_ACTIVITY_REPORT } from '../../queries';
import type { SetShowSnackbar } from '../../types';

export const SubscriptionActivityReportForm: FC<{
	facilityId: string;
	setShowSnackbar: SetShowSnackbar;
}> = ({ facilityId, setShowSnackbar }) => {
	const [accountId, setAccountId] = useState('');
	const [subscriptionName, setSubscriptionName] = useState('');
	const [isSummary, setIsSummary] = useState(true);
	const [showOutstandingBalances, setShowOutstandingBalances] = useState(false);
	const [startMonth, setStartMonth] = useState<string>();
	const [startYear, setStartYear] = useState<string>();
	const [endMonth, setEndMonth] = useState<string>();
	const [endYear, setEndYear] = useState<string>();
	const monthOptions = [
		{ label: 'January', value: '0' },
		{ label: 'February', value: '1' },
		{ label: 'March', value: '2' },
		{ label: 'April', value: '3' },
		{ label: 'May', value: '4' },
		{ label: 'June', value: '5' },
		{ label: 'July', value: '6' },
		{ label: 'August', value: '7' },
		{ label: 'September', value: '8' },
		{ label: 'October', value: '9' },
		{ label: 'November', value: '10' },
		{ label: 'December', value: '11' },
	];
	const getYearOptions = useCallback(() => {
		const optionstartYear = 2021;
		const optionEndYear = new Date().getFullYear() + 1;
		const yearOptions = [];
		for (let i = optionstartYear; i <= optionEndYear; i++) {
			yearOptions.push({ label: i.toString(), value: i });
		}
		return yearOptions;
	}, []);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
	const [getSubscriptionActivityReport] = useLazyQuery(GET_SUBSCRIPTION_ACTIVITY_REPORT, {
		fetchPolicy: 'network-only',
	});

	const getDownloadParams = useCallback(
		() => ({
			facilityId,
			isSummary,
			showOutstandingBalances,
			...(accountId && {
				accountId: Number.parseInt(
					accountId.toLowerCase().startsWith('ac-') ? accountId.slice(3) : accountId,
					10,
				),
			}),
			...(subscriptionName && { subscriptionName }),
			...(startMonth && { startMonth: Number.parseInt(startMonth, 10) }),
			...(startYear && { startYear: Number.parseInt(startYear, 10) }),
			...(endMonth && { endMonth: Number.parseInt(endMonth, 10) }),
			...(endYear && { endYear: Number.parseInt(endYear, 10) }),
		}),
		[
			facilityId,
			isSummary,
			showOutstandingBalances,
			accountId,
			subscriptionName,
			startMonth,
			startYear,
			endMonth,
			endYear,
		],
	);

	const params = getDownloadParams();

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: ` Parking start time should be before parking end time.`,
				failingCondition:
					startYear &&
					endYear &&
					(Number(startYear) > Number(endYear) ||
						(startYear === endYear && Number(startMonth) > Number(endMonth))),
			},
			{
				message: ` Start year must be selected`,
				failingCondition: startMonth && !startYear,
			},
			{
				message: ` End year must be selected`,
				failingCondition: endMonth && !endYear,
			},
			{
				message: ` Start month must be selected`,
				failingCondition: startYear && !startMonth,
			},
			{
				message: ` End month must be selected`,
				failingCondition: endYear && !endMonth,
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setIsDownloadingCsv(false);
			return false;
		}
		return true;
	}, [setShowSnackbar, startMonth, startYear, endMonth, endYear]);

	const handleReset = useCallback(() => {
		setAccountId('');
		setSubscriptionName('');
		setIsSummary(true);
		setShowOutstandingBalances(false);
		setStartMonth('');
		setStartYear('');
		setEndMonth('');
		setEndYear('');
	}, []);

	const handleDownload = useCallback(
		async (reportType: ReportType) => {
			if (reportType === ReportType.CSV) {
				setIsDownloadingCsv(true);
			} else {
				setIsDownloadingPdf(true);
			}

			const validationSuccessful = handleValidation();
			if (validationSuccessful) {
				const { error } = await getSubscriptionActivityReport({
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
					variables: {
						...params,
						reportType,
					},
					onCompleted: (data: { getSubscriptionActivityReport: { url: string } }) => {
						const fileLink = document.createElement('a');
						fileLink.href = data.getSubscriptionActivityReport.url;
						fileLink.click();
					},
				});
				if (error) {
					setShowSnackbar({
						message: error.message,
						severity: 'error',
						onClose: () => {
							setShowSnackbar(null);
						},
					});
				}

				setIsDownloadingCsv(false);
				setIsDownloadingPdf(false);
			}
		},
		[setShowSnackbar, getSubscriptionActivityReport, handleValidation, params],
	);

	const DNACNote = `Note: this report will not match the payment reports if the facility's accounting method is "do not apply cash".`;

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2} sx={{ marginBottom: (theme) => theme.spacing(3) }}>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>Start Month</InputLabel>
						<Select
							label='Start Month'
							value={startMonth || ''}
							onChange={(e) => setStartMonth(e.target.value)}
							data-cy='start-month-select-menu'
						>
							{monthOptions.map((month) => (
								<MenuItem key={month.value} value={month.value}>
									{month.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>Start Year</InputLabel>
						<Select
							label='Start Year'
							value={startYear || ''}
							onChange={(e) => setStartYear(e.target.value)}
							data-cy='start-year-select-menu'
						>
							{getYearOptions().map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ marginBottom: (theme) => theme.spacing(3) }}>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>End Month</InputLabel>
						<Select
							label='End Month'
							value={endMonth || ''}
							onChange={(e) => setEndMonth(e.target.value)}
							data-cy='end-month-select-menu'
						>
							{monthOptions.map((month) => (
								<MenuItem key={month.value} value={month.value}>
									{month.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>End Year</InputLabel>
						<Select
							label='End Year'
							value={endYear || ''}
							onChange={(e) => setEndYear(e.target.value)}
							data-cy='end-year-select-menu'
						>
							{getYearOptions().map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ marginBottom: (theme) => theme.spacing(3) }}>
				<Grid item xs={6}>
					<TextField
						type='number'
						fullWidth
						variant='outlined'
						label='Account Id'
						value={accountId}
						onChange={(e) => {
							setAccountId(e.target.value);
						}}
						inputProps={{ 'data-cy': 'account-id-input' }}
						onKeyDown={(event) => {
							if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
								event.preventDefault();
							}
						}}
						onPaste={(event) => {
							event.preventDefault();

							const paste = event.clipboardData.getData('text');
							const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

							if (cleanedPaste) {
								setAccountId(cleanedPaste[1]);
							}
						}}
						InputProps={{
							startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
						}}
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						fullWidth
						variant='outlined'
						label='Subscription Name'
						value={subscriptionName}
						onChange={(e) => {
							setSubscriptionName(e.target.value);
						}}
						inputProps={{ 'data-cy': 'subscription-name-input' }}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2}>
				<FormControlLabel
					sx={{ minWidth: '17em' }}
					value={isSummary}
					labelPlacement='start'
					label='Summarize Subscriptions: '
					control={
						<Switch
							checked={isSummary}
							onChange={(e) => setIsSummary(e.target.checked)}
							inputProps={{ 'aria-label': 'controlled' }}
							data-cy='summary-switch'
						/>
					}
				/>
			</Grid>
			<Grid container spacing={2}>
				<FormControlLabel
					sx={{ minWidth: '17em', marginTop: '1em' }}
					value={showOutstandingBalances}
					labelPlacement='start'
					label='Show Outstanding Balances: '
					control={
						<Switch
							checked={showOutstandingBalances}
							onChange={(e) => setShowOutstandingBalances(e.target.checked)}
							inputProps={{ 'aria-label': 'controlled' }}
							data-cy='outstanding-balances-switch'
						/>
					}
				/>
			</Grid>
			<Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', mt: 3 }}>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						onClick={() => handleDownload(ReportType.CSV)}
						disabled={!facilityId || isDownloadingCsv}
						data-cy='download-csv-button'
					>
						{isDownloadingCsv ? <CircularProgress size={24} /> : 'Download CSV'}
					</Button>
				</Grid>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						onClick={() => handleDownload(ReportType.PDF)}
						disabled={!facilityId || isDownloadingPdf}
						data-cy='download-csv-button'
					>
						{isDownloadingPdf ? <CircularProgress size={24} /> : 'Download PDF'}
					</Button>
				</Grid>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						fullWidth
						color='secondary'
						onClick={handleReset}
						data-cy='reset-button'
					>
						Reset
					</Button>
				</Grid>
			</Grid>
			<div style={{ marginTop: '1em', textAlign: 'center' }}>{DNACNote}</div>
		</Box>
	);
};
